import React, { useMemo, useState } from "react";
import {
  Rocket, ClipboardList, Store, MessageCircle, Printer, DollarSign,
  Megaphone, Users, Search, ChevronDown, LifeBuoy, Sparkles, X,
} from "lucide-react";
import { SECOES_AJUDA } from "./conteudo";
import { VIDEOS } from "./conteudo/videos";
import ReverTutoriais from "./ReverTutoriais";
import VideoTutorial from "./VideoTutorial";
import TodosOsVideos from "./TodosOsVideos";

// Ícones resolvidos por NOME: o conteúdo é dado puro (sem JSX) pra virar a
// base de conhecimento da Cleo quando esta aba virar conversa.
const ICONES = {
  Rocket, ClipboardList, Store, MessageCircle, Printer, DollarSign, Megaphone, Users,
};

const normalizar = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Um item pergunta/resposta com destaque de acordeão. */
function Topico({ topico, aberto, onToggle }) {
  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 py-3 px-4 text-left hover:bg-muted/50 transition-colors"
      >
        <ChevronDown
          className={`w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground transition-transform ${aberto ? "rotate-180" : ""}`}
        />
        <span className="text-sm font-medium flex-1">{topico.pergunta}</span>
      </button>
      {aberto && (
        <p className="px-11 pb-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
          {topico.resposta}
        </p>
      )}
      {aberto && topico.videos?.length > 0 && (
        <div className="px-11 pb-4 space-y-3">
          {topico.videos.map((id) => <VideoTutorial key={id} id={id} />)}
        </div>
      )}
    </div>
  );
}

/** Card de uma seção da Ajuda (grupo de perguntas). */
function Secao({ secao, busca, abertos, onToggle }) {
  const Icone = ICONES[secao.icone] || LifeBuoy;
  return (
    <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
      <div className="px-4 py-4 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icone className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-base leading-tight">{secao.titulo}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{secao.intro}</p>
          </div>
        </div>
      </div>
      <div>
        {secao.topicos.map((t, i) => {
          const chave = `${secao.id}:${i}`;
          return (
            <Topico
              key={chave}
              topico={t}
              aberto={busca ? true : abertos.has(chave)}
              onToggle={() => onToggle(chave)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function AjudaPage() {
  const [busca, setBusca] = useState("");
  const [abertos, setAbertos] = useState(() => new Set());

  const toggle = (chave) =>
    setAbertos((prev) => {
      const novo = new Set(prev);
      novo.has(chave) ? novo.delete(chave) : novo.add(chave);
      return novo;
    });

  // Busca POR PALAVRAS, não por frase inteira: "cupom não imprimiu" tem que
  // achar "o cupom não saiu". Cada palavra relevante (4+ letras) precisa
  // aparecer; palavras longas casam também pelo radical (imprimiu → imprim →
  // "imprime"/"imprimir"), tolerando conjugação. Com busca ativa, os
  // resultados aparecem já expandidos.
  // O título dos vídeos do tópico também conta na busca.
  const { secoesFiltradas, videosFiltrados } = useMemo(() => {
    const q = normalizar(busca.trim());
    if (!q) return { secoesFiltradas: SECOES_AJUDA, videosFiltrados: null };
    const palavras = q.split(/\s+/).filter((w) => w.length >= 4);
    const casa = (texto) =>
      palavras.length === 0
        ? texto.includes(q)
        : palavras.every(
            (w) => texto.includes(w) || (w.length >= 6 && texto.includes(w.slice(0, -2)))
          );
    const titulos = (t) => (t.videos || []).map((id) => VIDEOS[id]?.titulo || "").join(" ");
    return {
      secoesFiltradas: SECOES_AJUDA.map((s) => ({
        ...s,
        topicos: s.topicos.filter((t) =>
          casa(normalizar(`${s.titulo} ${t.pergunta} ${t.resposta} ${titulos(t)}`))
        ),
      })).filter((s) => s.topicos.length > 0),
      videosFiltrados: Object.keys(VIDEOS).filter((id) => casa(normalizar(VIDEOS[id].titulo))),
    };
  }, [busca]);

  const totalResultados = secoesFiltradas.reduce((n, s) => n + s.topicos.length, 0)
    + (videosFiltrados?.length || 0);

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
          <LifeBuoy className="w-7 h-7 text-primary" /> Ajuda
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Como cada parte do sistema funciona, explicado sem tecniquês. Use a busca
          pra ir direto ao ponto.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar na ajuda… (ex: cupom não imprimiu, taxa de entrega, pix)"
          className="w-full pl-10 pr-10 py-3 rounded-xl border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
        />
        {busca && (
          <button
            onClick={() => setBusca("")}
            aria-label="Limpar busca"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {busca.trim() && (
        <p className="text-xs text-muted-foreground -mt-2">
          {totalResultados === 0
            ? "Nada encontrado — tente outra palavra (ex: “impressora”, “entregador”, “promoção”)."
            : `${totalResultados} resposta${totalResultados === 1 ? "" : "s"} encontrada${totalResultados === 1 ? "" : "s"}.`}
        </p>
      )}

      {secoesFiltradas.map((s) => (
        <Secao key={s.id} secao={s} busca={busca.trim()} abertos={abertos} onToggle={toggle} />
      ))}

      <TodosOsVideos ids={videosFiltrados} />

      <ReverTutoriais />

      <div className="bg-card rounded-xl border shadow-sm p-5 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-semibold text-base">Não achou a resposta?</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Esta central cresce a cada versão — e em breve a <strong>Cleo</strong>, a
            mesma IA que atende seus clientes no WhatsApp, vai responder suas dúvidas
            aqui dentro, por conversa. Enquanto isso, fale com o suporte pelo WhatsApp
            que a gente resolve junto.
          </p>
        </div>
      </div>
    </div>
  );
}
